import 'controllers/common/turbo_modal/style.scss';
import { Controller } from '@hotwired/stimulus';
import DefaultFrame from 'controllers/common/turbo_modal/default_frame';
import SrcFrame from 'controllers/common/turbo_modal/src_frame';
import ScrollableContent from 'controllers/common/turbo_modal/scrollable_content';

export class TurboModalController extends Controller {
  static modalIdDatasetPropertyName = 'jsModal';

  static noIdValueErrorMessage(element) {
    return `\
TurboModalController:\
 missing \`data-turbo-modal-id-value\` attribute for element:
${element.outerHTML}
`;
  }

  static noFrameIdValueErrorMessage(element) {
    return `\
TurboModalController:\
 missing \`data-turbo-modal-frame-id-value\` attribute for element:
${element.outerHTML}
`;
  }

  static noModalElementErrorMessage(id) {
    return `\
TurboModalController: there is no turbo-modal element with id: \`${id}\`\
`;
  }

  static values = {
    id: String,
    frameId: String,
    frameSrc: String,
    removeContentOnClose: Boolean,
  };

  static targets = ['initialFrameContent', 'scrollableContent'];

  static get registry() {
    if (window.kajabiTurboModals === undefined) {
      window.kajabiTurboModals = {};
    }
    return window.kajabiTurboModals;
  }

  static registerModal({ id, modal }) {
    this.registry[id] = modal;
  }

  static registeredModal(id) {
    const modal = this.registry[id];

    if (modal === undefined) {
      throw new Error(this.noModalElementErrorMessage(id));
    }
    return modal;
  }

  static openModal(id) {
    this.registeredModal(id).open();
  }

  static closeModal(id) {
    this.registeredModal(id).close();
  }

  connect() {
    if (!this.hasIdValue) {
      throw new Error(this.constructor.noIdValueErrorMessage(this.element));
    }
    if (!this.hasFrameIdValue) {
      throw new Error(this.constructor.noFrameIdValueErrorMessage(this.element));
    }

    // Save off a global reference for the controller instance by its id value
    this.constructor.registerModal({ id: this.idValue, modal: this });

    // Set the Sage modal id dataset property so `open()` below works.
    const propertyName = this.constructor.modalIdDatasetPropertyName;
    this.element.dataset[propertyName] = this.idValue;

    // Default the removeContentOnCloseValue
    if (!this.hasRemoveContentOnCloseValue) {
      this.removeContentOnCloseValue = this.hasFrameSrcValue;
    }

    // Connect the turbo frame.
    if (this.hasFrameSrcValue) {
      this.frame = new SrcFrame({
        id: this.frameIdValue,
        src: this.frameSrcValue,
        initialContentElement: this.initialFrameContentTarget,
        removeContentOnClose: this.removeContentOnCloseValue,
      });
    } else {
      this.frame = new DefaultFrame({
        id: this.frameIdValue,
        initialContentElement: this.initialFrameContentTarget,
      });
    }

    // Setup open/closed state handling
    this.opened = false;
    this.boundOnSageModalCloseAll = this._onSageModalCloseAll.bind(this);
    document.addEventListener('sage.modal.closeAll', this.boundOnSageModalCloseAll);

    this.frame.onConnect();
  }

  disconnect() {
    document.removeEventListener('sage.modal.closeAll', this.boundOnSageModalCloseAll);
  }

  scrollableContentTargetConnected(element) {
    const scrollableContent = new ScrollableContent({ element });
    scrollableContent.onConnect();
  }

  open() {
    window.Sage.modal.openModal(this.idValue, this.element);
    this.frame.onOpen();
    this._afterOpen();
  }

  _afterOpen() {
    this.opened = true;
    this.dispatch('opened', {
      detail: {
        modalId: this.idValue,
      },
      bubbles: true,
    });
  }

  close() {
    window.Sage.modal.closeModal(this.element);
    this.frame.onClose();
    this._afterClose();
  }

  _afterClose() {
    this.opened = false;
    this.dispatch('closed', {
      detail: {
        modalId: this.idValue,
      },
      bubbles: true,
    });
  }

  _onSageModalCloseAll() {
    if (this.opened) {
      this._afterClose();
    }
  }
}

export { TurboModalController as default };
